import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Key, 
  RotateCcw, 
  ShieldOff, 
  ShieldCheck, 
  AlertTriangle, 
  CheckCircle2, 
  Copy, 
  Check,
  RefreshCw,
  Calendar
} from "lucide-react";

/**
 * WebhookKeyCard - Webhook key lifecycle management for GHL appointments
 * 
 * Located in Admin → Calendars tab
 * 
 * Features:
 * - View key fingerprint + status
 * - Rotate key (invalidate old, generate new)
 * - Revoke key (block all inbound)
 * - Reactivate revoked key
 * 
 * P0 TENANT ISOLATION: Accepts tenantId prop for SuperAdmin cross-tenant access.
 */
export default function WebhookKeyCard({ tenantId }) {
  const [webhookKey, setWebhookKey] = useState(null);
  const [calendarRoutesCount, setCalendarRoutesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [keyActionModal, setKeyActionModal] = useState({ open: false, action: null });
  const [keyActionLoading, setKeyActionLoading] = useState(false);
  const [newWebhookKey, setNewWebhookKey] = useState(null);
  const [copiedNewKey, setCopiedNewKey] = useState(false);
  const [copiedBearerKey, setCopiedBearerKey] = useState(false);

  const buildTenantParams = useCallback(() => {
    return tenantId ? { tenant_id: tenantId } : {};
  }, [tenantId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const params = buildTenantParams();
    try {
      const keyRes = await api.get("/webhooks/keys/gohighlevel_appointment", { params });
      setWebhookKey(keyRes.data);
      setCalendarRoutesCount(keyRes.data?.calendar_routes_count || 0);
    } catch {
      setWebhookKey(null);
      setCalendarRoutesCount(0);
    } finally {
      setLoading(false);
    }
  }, [buildTenantParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRotateKey = async () => {
    setKeyActionLoading(true);
    setNewWebhookKey(null);
    try {
      const params = buildTenantParams();
      const res = await api.post("/webhooks/keys/gohighlevel_appointment/rotate", null, { params });
      setNewWebhookKey(res.data.key);
      toast({
        title: "Webhook Key Rotated",
        description: "The old key is now invalid. Store the new key securely.",
      });
      loadData();
    } catch (err) {
      toast({
        title: "Failed to rotate key",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setKeyActionLoading(false);
    }
  };

  const handleRevokeKey = async () => {
    setKeyActionLoading(true);
    try {
      const params = buildTenantParams();
      await api.post("/webhooks/keys/gohighlevel_appointment/revoke", null, { params });
      toast({
        title: "Webhook Key Revoked",
        description: "All inbound appointment webhooks will now fail authentication.",
      });
      setKeyActionModal({ open: false, action: null });
      loadData();
    } catch (err) {
      toast({
        title: "Failed to revoke key",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setKeyActionLoading(false);
    }
  };

  const handleReactivateKey = async () => {
    setKeyActionLoading(true);
    setNewWebhookKey(null);
    try {
      const params = buildTenantParams();
      const res = await api.post("/webhooks/keys/gohighlevel_appointment/reactivate", null, { params });
      setNewWebhookKey(res.data.key);
      toast({
        title: "Webhook Key Reactivated",
        description: "Store this new key securely - it won't be shown again.",
      });
      loadData();
    } catch (err) {
      toast({
        title: "Failed to reactivate key",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setKeyActionLoading(false);
    }
  };

  const openKeyActionModal = (action) => {
    setNewWebhookKey(null);
    setCopiedNewKey(false);
    setCopiedBearerKey(false);
    setKeyActionModal({ open: true, action });
  };

  const closeKeyActionModal = () => {
    setKeyActionModal({ open: false, action: null });
    setNewWebhookKey(null);
    setCopiedNewKey(false);
    setCopiedBearerKey(false);
  };

  const copyNewKey = async () => {
    if (!newWebhookKey) return;
    try {
      await navigator.clipboard.writeText(newWebhookKey);
      setCopiedNewKey(true);
      setTimeout(() => setCopiedNewKey(false), 2000);
      toast({ title: "Copied!", description: "API key copied to clipboard" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const copyBearerKey = async () => {
    if (!newWebhookKey) return;
    try {
      await navigator.clipboard.writeText(`Bearer ${newWebhookKey}`);
      setCopiedBearerKey(true);
      setTimeout(() => setCopiedBearerKey(false), 2000);
      toast({ title: "Copied!", description: "Bearer token copied to clipboard" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <Card className="glass border-white/10">
        <CardContent className="py-8 flex items-center justify-center">
          <RefreshCw className="h-5 w-5 animate-spin text-indigo-400" />
          <span className="ml-2 text-slate-400">Loading...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="glass border-white/10" data-testid="webhook-key-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl text-white flex items-center gap-2">
                <Calendar className="h-5 w-5 text-indigo-400" />
                Calendars
              </CardTitle>
              <CardDescription className="text-slate-400 mt-1">
                Add calendars to receive incoming webhooks and War Room reporting
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {webhookKey ? (
            <>
              {/* Key Status */}
              <div className="bg-slate-800/50 rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Key className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-300">Webhook Authentication Key</span>
                  </div>
                  <Badge className={`text-xs ${
                    webhookKey.status === "revoked" 
                      ? "bg-red-500/20 text-red-300" 
                      : "bg-emerald-500/20 text-emerald-300"
                  }`}>
                    {webhookKey.status || "active"}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Fingerprint:</span>
                    <code className="text-amber-300 font-mono">
                      {webhookKey.key_fingerprint || "********"}••••••••
                    </code>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Calendars:</span>
                    <span className="text-slate-200">{calendarRoutesCount}</span>
                  </div>
                </div>

                {/* Lifecycle Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                  {webhookKey.status === "revoked" ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openKeyActionModal("reactivate")}
                      className="h-8 text-xs border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                      data-testid="reactivate-webhook-key-btn"
                    >
                      <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
                      Reactivate Key
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openKeyActionModal("rotate")}
                        className="h-8 text-xs border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                        data-testid="rotate-webhook-key-btn"
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                        Rotate Key
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openKeyActionModal("revoke")}
                        className="h-8 text-xs border-red-500/30 text-red-300 hover:bg-red-500/10"
                        data-testid="revoke-webhook-key-btn"
                      >
                        <ShieldOff className="h-3.5 w-3.5 mr-1.5" />
                        Revoke Key
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="bg-slate-800/50 rounded-lg p-4 text-center">
              <Key className="h-8 w-8 text-slate-500 mx-auto mb-2" />
              <p className="text-sm text-slate-400">
                No webhook key configured yet.
              </p>
              <p className="text-xs text-slate-500 mt-1">
                A key will be automatically created when you add your first calendar route.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Webhook Key Lifecycle Modal */}
      <Dialog open={keyActionModal.open} onOpenChange={(open) => !open && closeKeyActionModal()}>
        <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <Key className="h-5 w-5" />
              {keyActionModal.action === "rotate" && "Rotate Webhook Key"}
              {keyActionModal.action === "revoke" && "Revoke Webhook Key"}
              {keyActionModal.action === "reactivate" && "Reactivate Webhook Key"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              GHL Appointment Webhook Authentication Key
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Show new key after rotate/reactivate */}
            {newWebhookKey ? (
              <div className="space-y-3">
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <p className="text-sm text-emerald-300 mb-2">
                    ✓ {keyActionModal.action === "rotate" ? "Key Rotated" : "Key Reactivated"} Successfully
                  </p>
                  <p className="text-xs text-slate-400">
                    Store this key securely — it will not be shown again.
                  </p>
                </div>
                
                {/* Raw Key */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-400">New API Key</label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={newWebhookKey}
                      readOnly
                      className="bg-slate-900 border-slate-700 text-slate-100 font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyNewKey}
                      className="border-slate-600 text-slate-200 hover:bg-slate-800 shrink-0"
                    >
                      {copiedNewKey ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                
                {/* Bearer Token */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-400">Authorization Header Value</label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={`Bearer ${newWebhookKey}`}
                      readOnly
                      className="bg-slate-900 border-slate-700 text-amber-300 font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyBearerKey}
                      className="border-slate-600 text-slate-200 hover:bg-slate-800 shrink-0"
                    >
                      {copiedBearerKey ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Copy this value directly into GHL's Authorization header field.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Rotate action */}
                {keyActionModal.action === "rotate" && (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-300">
                      Rotating the webhook key will immediately invalidate the current key.
                    </p>
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <p className="text-xs text-amber-300 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        All GHL webhook calls using the old key will fail immediately.
                      </p>
                    </div>
                    <p className="text-xs text-slate-400">
                      You will need to update the Authorization header in GHL with the new key.
                    </p>
                  </div>
                )}
                
                {/* Revoke action */}
                {keyActionModal.action === "revoke" && (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-300">
                      Revoking the webhook key will immediately block all inbound appointment webhooks.
                    </p>
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-xs text-red-300 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        All {calendarRoutesCount} calendar(s) will stop receiving data.
                      </p>
                    </div>
                    <p className="text-xs text-slate-400">
                      You can reactivate later by generating a new key.
                    </p>
                  </div>
                )}
                
                {/* Reactivate action */}
                {keyActionModal.action === "reactivate" && (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-300">
                      Generate a new key to reactivate webhook authentication.
                    </p>
                    <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                      <p className="text-xs text-emerald-300 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" />
                        Your calendars will start receiving data again.
                      </p>
                    </div>
                    <p className="text-xs text-slate-400">
                      The new key will only be shown once. Store it securely.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={closeKeyActionModal}
              className="border-slate-600 text-slate-200 hover:bg-slate-800"
            >
              {newWebhookKey ? "Done" : "Cancel"}
            </Button>
            {!newWebhookKey && (
              <>
                {keyActionModal.action === "rotate" && (
                  <Button
                    onClick={handleRotateKey}
                    disabled={keyActionLoading}
                    className="bg-amber-500/90 text-white hover:bg-amber-500"
                  >
                    {keyActionLoading ? "Rotating..." : "Rotate Key"}
                  </Button>
                )}
                {keyActionModal.action === "revoke" && (
                  <Button
                    onClick={handleRevokeKey}
                    disabled={keyActionLoading}
                    variant="destructive"
                    className="bg-red-500/90 text-white hover:bg-red-500"
                  >
                    {keyActionLoading ? "Revoking..." : "Revoke Key"}
                  </Button>
                )}
                {keyActionModal.action === "reactivate" && (
                  <Button
                    onClick={handleReactivateKey}
                    disabled={keyActionLoading}
                    className="bg-emerald-500/90 text-white hover:bg-emerald-500"
                  >
                    {keyActionLoading ? "Reactivating..." : "Reactivate Key"}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
